import { Box, Typography } from "@mui/material";
import {
  mapMerchantsFiltersToParams,
  mapMerchantsSortColumn,
  useMerchantsListQuery,
} from "Api/Queries/Merchants/useGetMerchantsQuery";
import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import { LICENSE_CODE_TO_DATA_MAP } from "Components/Licenses/licenseUtils";
import { SettlementPaymentCell } from "Components/Users/UserListTable/SettlementPaymentCell";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import {
  type MerchantListItemDto,
  MerchantStatus,
  MerchantViewMode,
  type PartyLicenseDto,
  SettlementStatus,
  type PhoneNumber,
} from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { type ReactNode, useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import { useServerTable } from "Hooks/useServerTable";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.ListPage.Table;

type Props = {
  licensePublicID?: string;
  ownOnly?: boolean;
  license?: PartyLicenseDto;
  density?: "compact" | "comfortable";
  title?: ReactNode;
};

export const MerchantsTable = ({
  licensePublicID,
  ownOnly,
  license,
  density,
  title,
}: Props) => {
  const { queryParams, tableProps } = useServerTable<MerchantListItemDto>({
    defaultSorting: [{ id: "dateCreated", desc: true }],
  });

  const { data: queryData, isLoading } = useMerchantsListQuery({
    ownOnly,
    licensePublicID,
    viewMode: MerchantViewMode.HunterView,
    offset: queryParams.offset,
    limit: queryParams.limit,
    sortBy: mapMerchantsSortColumn(queryParams.sortBy),
    sortDirection: queryParams.sortDirection,
    ...mapMerchantsFiltersToParams(queryParams.filters),
  });

  const data = queryData?.items ?? [];
  const rowCount = queryData?.totalCount ?? 0;
  const { t } = useResource();
  const getResponsiveValue = useResponsiveValue();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const columns = useMemo<Array<MRT_ColumnDef<MerchantListItemDto>>>(() => {
    return [
      {
        accessorKey: "merchantStatus",
        header: t(COMPONENT_RESOURCES.Columns.MerchantStatus),
        size: 1,
        Cell: ({ row }) => (
          <MerchantStatusCell status={row.original.merchantStatus} />
        ),
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(
              Resources.Merchant.MerchantStatus[
                MerchantStatus.InvitedWaitingForConfirmation
              ],
            ),
            value: MerchantStatus.InvitedWaitingForConfirmation,
          },
          {
            label: t(Resources.Merchant.MerchantStatus[MerchantStatus.Active]),
            value: MerchantStatus.Active,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus[MerchantStatus.Onboarding],
            ),
            value: MerchantStatus.Onboarding,
          },
        ],
      },
      {
        accessorKey: "party.companyName",
        header: t(COMPONENT_RESOURCES.Columns.Party.CompanyName),
        size: getResponsiveValue([150, 200]),
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      // {
      //   accessorKey: "contactPerson",
      //   header: "Kontaktní osoba",
      //   size: 200,
      // },
      {
        accessorKey: "party.phone",
        header: t(COMPONENT_RESOURCES.Columns.Party.Phone),
        size: getResponsiveValue([120, 140]),
        enableSorting: false,
        Cell: ({ cell }) => formatPhone(cell.getValue<PhoneNumber>()),
      },
      {
        accessorKey: "party.email",
        header: t(COMPONENT_RESOURCES.Columns.Party.Email),
        size: 250,
        enableSorting: false,
        // Cell: ({ cell }) => (
        //   <Tooltip title={cell.getValue<string>()}>
        //     <span
        //       style={{
        //         display: "block",
        //         maxWidth: 200,
        //         whiteSpace: "nowrap",
        //         overflow: "hidden",
        //         textOverflow: "ellipsis",
        //       }}
        //     >
        //       {cell.getValue<string>()}
        //     </span>
        //   </Tooltip>
        // ),
      },
      {
        accessorKey: "transactionsCount",
        header: t(COMPONENT_RESOURCES.Columns.TransactionsCount),
        size: 170,
      },
      {
        accessorKey: "party.settlementStatus",
        header: t(COMPONENT_RESOURCES.Columns.Party.SettlementStatus),
        size: 160,
        enableSorting: false,
        Cell: ({ row }) => <SettlementPaymentCell party={row.original.party} />,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Paid
              ],
            ),
            value: SettlementStatus.Paid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Unpaid
              ],
            ),
            value: SettlementStatus.Unpaid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Overdue
              ],
            ),
            value: SettlementStatus.Overdue,
          },
        ],
      },
    ];
  }, [getResponsiveValue, t]);

  const { title: licenseTitle, czImg } = license
    ? LICENSE_CODE_TO_DATA_MAP[license.code]
    : { title: "", czImg: "" };

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: {
      ...tableProps.initialState,
      density: density || getResponsiveValue(["compact", "comfortable"]),
    },
    state: {
      ...tableProps.state,
      isLoading,
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("merchant-detail")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: {
          cursor: "pointer",
        },
      };
    },
    muiTopToolbarProps: {
      sx: {
        "& > div": {
          p: 0, // Remove padding inside the wrapper div to move the license image all the way to the border
        },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box display="flex" gap="1rem" alignItems="center">
        {title ? (
          <Box
            height="44px"
            display="flex"
            alignItems="center"
            p={({ spacing }) => spacing(1, 2, 1, 0)}
            gap={3}
          >
            {czImg && (
              <Box
                component="img"
                onDragStart={(e) => e.preventDefault()}
                width={["60px", "100px"]}
                src={czImg}
                aria-hidden
                alt=""
              />
            )}
            <Typography variant="h2" lineHeight={1.4} fontSize={[16, 18, 22]}>
              {`${title} ${license?.licenseNo ? `no. ${license.licenseNo}` : ""}`}
            </Typography>
          </Box>
        ) : (
          <Box
            height="44px"
            display="flex"
            alignItems="center"
            p={({ spacing }) => spacing(1, 2, 1, 0)}
            gap={3}
          >
            {czImg && (
              <Box
                component="img"
                onDragStart={(e) => e.preventDefault()}
                width={["60px", "100px"]}
                src={czImg}
                aria-hidden
                alt=""
              />
            )}
            <Typography variant="h2" lineHeight={1.4} fontSize={[16, 18, 22]}>
              {`${licenseTitle} ${license?.licenseNo ? `no. ${license.licenseNo}` : ""}`}
            </Typography>
          </Box>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  return <MaterialReactTable table={table} />;
};
